/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */
/**
 * The Storage Client handles saving and retrieving data for your smart desktop.
 *
 *
 * See the <a href="/docs/enterprise/storage/storingData">Storing Data tutorial</a> for an overview of using the Storage Client.
 * @module StorageClient
 */

import { Logger } from "./logger";
import { promisify } from "../common/util";
import { ComponentMutateParams } from "../services/window/WindowAbstractions/BaseWindow";
import { RouterClient } from "./routerClient";
import SystemManagerClient from "../common/systemManagerClient";
import { StandardError, StandardErrorCallback, StandardPromise } from "../types";
import pLimit from "p-limit";

const limit = pLimit(1);

/**
 * Logs an "info" level log unless it's an error, in which case it logs a "error" level log
 */
const log = (err: StandardError, ...args) => {
	const logMethod = err ? Logger.system.error : Logger.system.info;
	logMethod(...args, err);
};

/**
 * Define the user name for storage (i.e., each user has unique storage).
 *
 * ```javascript
 * FSBL.Clients.StorageClient.setUser({ user: "JohnDeere"});
 * ```
 * @param params
 * @param params.user A unique key to store user data under
 * @param cb Callback to be called on success.
 */
export const setUser = async (params: { user: string }, cb?: StandardErrorCallback<void>): StandardPromise<void> => {
	await SystemManagerClient.waitForStartup("storageService");
	const { err, response } = await RouterClient.query("Storage.setUser", { user: params.user });
	log(err, "APPLICATION LIFECYCLE:StorageClient.setUser", params, err, response.data);
	cb?.(err);
	return { err };
};

/**
 * @deprecated This method has been deprecated.
 *
 * Specifies the data store. For normal operation this function doesn't have to be invoked -- the default data store is set in configuration.
 *
 * ```javascript
 * FSBL.Clients.StorageClient.setStore({topic:"finsemble", dataStore:"redis"})
 * ```
 * @param params
 * @param params.topic If specified then data store is set only for topic.
 * @param params.dataStore Identifies the data store (e.g. "localStorage", "redis").
 * @param cb Callback to be called on success.
 * @ignore
 */
export const setStore = async (
	params: { topic: string; dataStore?: string },
	cb?: StandardErrorCallback<void>
): StandardPromise<void> => {
	Logger.deprecated("StorageClient.setStore() has been deprecated and will be removed in a future release");
	Logger.system.log("APPLICATION LIFECYCLE:StorageClient.setStore", params, cb);
	await SystemManagerClient.waitForStartup("storageService");
	const { err, response } = await RouterClient.query("Storage.setStore", params);
	log(err, "Storage.setStore", response);
	// Always returns { status: "success"}. Not useful but leaving for strict backward compatibility.
	cb?.(err, response.data);
	return { err, data: response.data };
};

/**
 * Save a key value pair into storage.
 *
 * ```javascript
 * FSBL.Clients.StorageClient.save({topic:"finsemble", key:"testKey", value:"testValue"});
 * FSBL.Clients.StorageClient.save({topic:"finsemble", key:"testKey", value:"testValue"}, (err, data) => {});
 * const {err, data} = await FSBL.Clients.StorageClient.save({topic:"finsemble", key:"testKey", value:"testValue"});
 * ```
 * @param params
 * @param params.topic Storage topic for key being stored.
 * @param params.key The key for the value to be stored under.
 * @param params.value The value to be stored.
 * @param cb Callback to be called on success.
 */
export const save = async (params: ComponentMutateParams, cb?: StandardErrorCallback<void>): StandardPromise<void> => {
	if (typeof params.key !== "string" || typeof params.topic !== "string") {
		throw new Error("Values for key and topic must be strings.");
	}
	await SystemManagerClient.waitForStartup("storageService");
	const { err, response } = await RouterClient.query("Storage.save", params);
	log(err, "Storage.save", response.data);
	// data is not useful in response but retaining for strict backward compatibility
	cb?.(err, response.data);
	if (err) throw err; // Backward compatibility
	return { err, data: response.data };
};

/**
 * Get a value from storage.
 *
 *
 * ```javascript
 * FSBL.Clients.StorageClient.getStandardized({ topic:"finsemble", key:"testKey" }, function(err, data) {
 *	let myData = data;
 * });
 * const {err, data} = await FSBL.Clients.StorageClient.getStandardized({ topic:"finsemble", key:"testKey" });
 * ```
 * @param params
 * @param params.key The key to get from storage.
 * @param params.topic The topic that the data is saved under.
 * @param cb Callback to be called on success.
 */
export const getStandardized = async <T = any>(
	params: { key: string; topic: string },
	cb?: StandardErrorCallback<T>
): StandardPromise<T> => {
	if (typeof params.key !== "string" || typeof params.topic !== "string") {
		throw new Error("Values for key and topic must be strings.");
	}

	await SystemManagerClient.waitForStartup("storageService");
	const { err, response } = await RouterClient.query("Storage.get", params);
	log(err, "Storage.get", response.data);
	cb?.(err, response.data);
	return { err, data: response.data };
};

/**
 * @deprecated
 * Please use getStandardized() for a standardized `Promise<{err, data}>`
 * This function returns a `Promise<T>` and rejects on an error. This is maintained for backward compatibility.
 *
 * Get a value from storage.
 *
 * ```javascript
 * FSBL.Clients.StorageClient.get({ topic:"finsemble", key:"testKey" }, function(err, data) {
 *	var myData = data;
 * });
 * ```
 *
 * @param params
 * @param params.key The key to get from storage.
 * @param params.topic The topic that the data is saved under.
 * @param cb Callback to be called on success.
 */
export const get = async <T = any>(
	params: { key: string; topic: string },
	cb?: StandardErrorCallback<T>
): Promise<T> => {
	const { err, data } = await getStandardized(params);
	cb?.(err, data);
	if (err) throw err;
	// backward compatibility
	else return data;
};

/**
 * Get all keys for the topic.
 *
 * ```javascript
 * FSBL.Clients.StorageClient.keys({topic:"finsemble", keyPrefix:"test"}, function(err, data){
 *	let myKeys = data;
 * });
 * const {err, data} = await FSBL.Clients.StorageClient.keys({topic:"finsemble", keyPrefix:"test"});
 * ```
 * @param params
 * @param params.topic Topic for the keys to return.
 * @param params.keyPrefix Filter all keys that don't start with this prefix.
 * @param cb Callback to be called on success.
 */
export const keys = async (
	params: { topic: string; keyPrefix?: string },
	cb?: StandardErrorCallback<string[]>
): StandardPromise<string[]> => {
	Logger.system.debug("StorageClient.keys", params, cb);
	await SystemManagerClient.waitForStartup("storageService");
	const { err, response } = await RouterClient.query("Storage.keys", params);
	log(err, "Storage.keys", response.data);
	cb?.(err, response.data);
	return { err, data: response.data };
};

/**
 * Get a multiple values from storage based on regex.(coming soon)
 * @param params
 * @param cb Callback to be called on success.
 * @ignore
 * @deprecated
 */
export const getMultiple = async (params, cb?: StandardErrorCallback<any>): StandardPromise<any> => {
	Logger.deprecated("StorageClient.getMultiple() is deprecated and will be removed in a future release");
	Logger.system.info("StorageClient.getMultiple", params, cb);
	await SystemManagerClient.waitForStartup("storageService");
	const { err, response } = await RouterClient.query("Storage.getMultiple", params);
	log(err, "StorageClient.getMultiple:", params, response);
	cb?.(err, response); // Strict backward compatibility
	return { err, data: response.data };
};

/**
 * Delete a value from storage.
 *
 * ```javascript
 * FSBL.Clients.StorageClient.remove({ key:"testKey" })
 * await FSBL.Clients.StorageClient.remove({ key:"testKey" })
 * ```
 * @param params
 * @param params.key The key to get from storage.
 * @param params.topic The topic that the data is saved under.
 * @param cb Callback to be called on success.
 */
export const remove = async (
	params: { key: string; topic: string },
	cb?: StandardErrorCallback<void>
): StandardPromise<void> => {
	await SystemManagerClient.waitForStartup("storageService");
	const { err, response } = await RouterClient.query("Storage.delete", params);
	log(err, "StorageClient.delete", response.data);
	cb?.(err, response.data);
	if (err) throw err; // Strict backward compatibility
	return { err, data: response.data };
};

/**
 * Rate limited storage save
 * @deprecated
 */
const save1 = (params: { key: string; topic: string; value: any }) => {
	Logger.deprecated("StorageClient.save1() is deprecated and will be removed in a future release");
	limit(() => save(params));
};

/**
 * Rate limited get. This will be moved into windowStorageManager
 * @deprecated
 */
const get1 = <T = any>(params: { key: string; topic: string }, cb?: StandardErrorCallback<T>): Promise<T> => {
	Logger.deprecated("StorageClient.get1() is deprecated and will be removed in a future release");
	return limit(() => get(params, cb));
};

/**
 * @deprecated
 */
const keys1 = (params: { topic: string; keyPrefix?: string }): Promise<string[]> => {
	Logger.deprecated("StorageClient.keys1() is deprecated and will be removed in a future release");
	return limit(() => promisify(keys)(params));
};

/**
 * Rate limited remove()
 * @deprecated
 */
const remove1 = (params: { key: string; topic: string }) => {
	Logger.deprecated("StorageClient.remove1() is deprecated and will be removed in a future release");
	limit(() => remove(params));
};

/**
 * @deprecated
 */
export const deleteInner = (...args) => {
	Logger.deprecated("StorageClient.delete() is deprecated and will be removed in a future release");
	(remove as any).apply(args);
};

/**
 * Clears a storage adapter of all data.
 * @param cb The callback to be invoked after the method completes successfully.
 */
export const clearCache = async (cb?: StandardErrorCallback<void>): StandardPromise<void> => {
	Logger.system.log("StorageClient.clearCache", cb);
	await SystemManagerClient.waitForStartup("storageService");
	const { err, response } = await RouterClient.query("Storage.clearCache", null);
	log(err, "StorageClient.clearCache", response.data);
	cb?.(err, response.data); // return result is not useful but keeping for strict backward compatibility
	return { err, data: response.data };
};

/**
 * @ignore
 */
export const StorageClient = {
	/**
	 * For backward compatibility
	 * @private
	 * @deprecated
	 */
	initialize: () => {
		Logger.system.deprecated("It is no longer necessary to call StorageClient.initialized()");
	},
	/**
	 * For backward compatibility
	 * @private
	 * @deprecated
	 */
	onReady: (cb?: any) => {
		Logger.system.deprecated("It is no longer necessary to call StorageClient.onReady()");
		cb?.();
	},
	clearCache,
	remove,
	getMultiple,
	keys,
	getStandardized,
	get,
	save,
	setStore,
	setUser,
	delete: deleteInner,
	remove1,
	get1,
	save1,
	keys1,
};

/**
 * @ignore
 */
export default StorageClient;
